import { useState } from "react";
import { Volume2, Play, Check, X, RotateCcw, Headphones, Eye, EyeOff } from "lucide-react";

const exercises = [
  {
    id: 1,
    level: "beginner",
    sentence: "I would like a cup of tea, please.",
    blanks: "I would like a ___ of tea, ___.",
    answers: ["cup", "please"],
    hint: "A container for drinking + a polite word",
  },
  {
    id: 2,
    level: "beginner",
    sentence: "She goes to school every morning.",
    blanks: "She ___ to school every ___.",
    answers: ["goes", "morning"],
    hint: "Verb for 'go' with she + time of day",
  },
  {
    id: 3,
    level: "beginner",
    sentence: "The children are playing in the garden.",
    blanks: "The children are ___ in the ___.",
    answers: ["playing", "garden"],
    hint: "An activity + an outdoor place at home",
  },
  {
    id: 4,
    level: "intermediate",
    sentence: "Could you please pass me the salt?",
    blanks: "___ you please ___ me the salt?",
    answers: ["Could", "pass"],
    hint: "A polite modal verb + verb meaning 'give/hand'",
  },
  {
    id: 5,
    level: "intermediate",
    sentence: "I have been waiting for thirty minutes.",
    blanks: "I have been ___ for ___ minutes.",
    answers: ["waiting", "thirty"],
    hint: "Present perfect continuous verb + a number",
  },
  {
    id: 6,
    level: "advanced",
    sentence: "If I had known earlier, I would have helped you.",
    blanks: "If I had ___ earlier, I would have ___ you.",
    answers: ["known", "helped"],
    hint: "Past participle of 'know' + past participle of 'help'",
  },
];

function speak(text, rate = 0.8) {
  if ("speechSynthesis" in window) {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = rate;
    u.lang = "en-US";
    window.speechSynthesis.speak(u);
  }
}

export default function Listening() {
  const [current, setCurrent] = useState(0);
  const [inputs, setInputs] = useState({});
  const [checked, setChecked] = useState(false);
  const [showSentence, setShowSentence] = useState(false);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);

  const ex = exercises[current];

  function handleCheck() {
    const correct = ex.answers.every(
      (ans, i) => (inputs[i] || "").trim().toLowerCase() === ans.toLowerCase()
    );
    if (correct) setScore((s) => s + 1);
    setChecked(true);
  }

  function handleNext() {
    if (current < exercises.length - 1) {
      setCurrent((c) => c + 1);
      setInputs({});
      setChecked(false);
      setShowSentence(false);
    } else {
      setCompleted(true);
    }
  }

  function handleRestart() {
    setCurrent(0);
    setInputs({});
    setChecked(false);
    setShowSentence(false);
    setScore(0);
    setCompleted(false);
  }

  if (completed) {
    const pct = Math.round((score / exercises.length) * 100);
    return (
      <div className="max-w-lg mx-auto text-center py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <Headphones className="w-12 h-12 text-gold-700 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Listening Complete!</h2>
          <p className="text-4xl font-bold text-gold-700 mb-2">{score}/{exercises.length}</p>
          <p className="text-gray-500 mb-6">
            {pct === 100 ? "Perfect ears! You caught every word!" : pct >= 60 ? "Good listening! Keep practicing." : "Keep at it! Listen more carefully next time."}
          </p>
          <button onClick={handleRestart} className="inline-flex items-center gap-2 bg-ink-900 text-white px-6 py-3 rounded-xl font-semibold hover:bg-ink-900 transition-all">
            <RotateCcw className="w-4 h-4" /> Try Again
          </button>
        </div>
      </div>
    );
  }

  // Split blanks into parts
  const parts = ex.blanks.split("___");
  let blankIndex = 0;

  return (
    <div className="space-y-6 page-enter relative">
      <div className="blob blob-blue" style={{ width: 320, height: 320, top: -40, right: -40, opacity: 0.18 }} />
      <div className="blob blob-purple" style={{ width: 260, height: 260, top: 280, left: -40, opacity: 0.15 }} />
      <div className="relative">
        <h1 className="font-display text-4xl md:text-5xl font-normal mb-2"><em>Listening.</em></h1>
        <p className="text-gray-600 dark:text-gray-300 font-semibold">Listen to the sentence and fill in the missing words.</p>
      </div>

      {/* Progress */}
      <div className="flex gap-1.5">
        {exercises.map((_, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full ${i < current ? "bg-sage-light" : i === current ? "bg-ink-900" : "bg-gray-200"}`} />
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
              ex.level === "beginner" ? "bg-sage-light text-sage-dark" : ex.level === "intermediate" ? "bg-gold-100 text-gold-700" : "bg-berry-light text-berry"
            }`}>{ex.level}</span>
            <span className="text-sm text-gray-400">{current + 1} / {exercises.length}</span>
          </div>
        </div>

        {/* Listen Buttons */}
        <div className="flex gap-3 mb-6">
          <button onClick={() => speak(ex.sentence, 0.7)} className="inline-flex items-center gap-2 bg-ink-900 text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-ink-900 hover:scale-105 transition-all">
            <Play className="w-4 h-4" /> Play Normal
          </button>
          <button onClick={() => speak(ex.sentence, 0.5)} className="inline-flex items-center gap-2 bg-cream-100 text-gold-700 px-5 py-2.5 rounded-xl font-semibold hover:bg-cream-200 transition-all">
            <Volume2 className="w-4 h-4" /> Play Slow
          </button>
          <button onClick={() => setShowSentence(!showSentence)} className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2.5 rounded-xl font-semibold hover:bg-gray-200 transition-all">
            {showSentence ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {showSentence ? "Hide" : "Peek"}
          </button>
        </div>

        {/* Show sentence if peeking */}
        {showSentence && (
          <div className="bg-gold-50 border border-gold-300 rounded-xl p-4 mb-6">
            <p className="text-gold-700 font-medium">{ex.sentence}</p>
          </div>
        )}

        {/* Fill in blanks */}
        <div className="bg-gray-50 rounded-xl p-6 mb-6">
          <p className="text-lg text-gray-800 leading-relaxed">
            {parts.map((part, i) => {
              const bi = blankIndex;
              if (i < parts.length - 1) blankIndex++;
              return (
                <span key={i}>
                  {part}
                  {i < parts.length - 1 && (
                    <input
                      type="text"
                      value={inputs[bi] || ""}
                      onChange={(e) => setInputs({ ...inputs, [bi]: e.target.value })}
                      disabled={checked}
                      placeholder="___"
                      className={`inline-block w-28 mx-1 px-3 py-1.5 border-2 rounded-lg text-center font-medium focus:outline-none transition-colors ${
                        checked
                          ? (inputs[bi] || "").trim().toLowerCase() === ex.answers[bi].toLowerCase()
                            ? "border-sage bg-sage-light text-sage-dark"
                            : "border-berry-light bg-berry-light text-berry-dark"
                          : "border-gold-500 bg-white focus:border-gold-500"
                      }`}
                    />
                  )}
                </span>
              );
            })}
          </p>
        </div>

        {/* Hint */}
        <p className="text-sm text-gray-400 mb-4">Hint: {ex.hint}</p>

        {/* Actions */}
        <div className="flex gap-3">
          {!checked ? (
            <button onClick={handleCheck} disabled={Object.keys(inputs).length < ex.answers.length} className="inline-flex items-center gap-2 bg-sage text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-sage transition-all disabled:opacity-50 disabled:cursor-not-allowed">
              <Check className="w-4 h-4" /> Check
            </button>
          ) : (
            <button onClick={handleNext} className="inline-flex items-center gap-2 bg-ink-900 text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-ink-900 transition-all">
              {current < exercises.length - 1 ? "Next" : "Finish"}
            </button>
          )}
        </div>

        {/* Result */}
        {checked && (
          <div className="mt-4">
            {ex.answers.every((ans, i) => (inputs[i] || "").trim().toLowerCase() === ans.toLowerCase()) ? (
              <div className="bg-sage-light border border-sage-light rounded-xl p-4 flex items-center gap-2">
                <Check className="w-5 h-5 text-sage-dark" />
                <p className="text-sage-dark font-semibold">Correct! Well done!</p>
              </div>
            ) : (
              <div className="bg-berry-light border border-berry-light rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <X className="w-5 h-5 text-berry" />
                  <p className="text-berry font-semibold">Not quite right.</p>
                </div>
                <p className="text-gray-700">Correct: <strong>{ex.sentence}</strong></p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
